// D&D Beyond live importer: asks the connector which sheets are open, scans
// the active campaign's roster (or the open tabs), streams progress, and
// merges the reads into the layout on Import.
import { useEffect, useRef, useState } from "react";
import { newRequestId, request, useBridgeStatus } from "../bridge";
import { mergeSheet, type SheetRead } from "../state/merge";
import { noteSheetHolds, statusSignature } from "../state/ddbLive";
import { useStore } from "../state/store";
import { campaignRoster, claimForCampaign, prunableNotOpen, unclaimFromCampaign } from "../state/campaign";
import type { Character } from "../state/types";
import { useFocusTrap } from "./FocusTrap";

type Scope = "campaign" | "open";
interface OpenTarget { sourceId: string; name?: string; url?: string }

export function SheetImporter({ onClose }: { onClose: () => void }) {
  const { layout, update, logActivity } = useStore();
  const bridge = useBridgeStatus();
  const campaign = layout.campaignSnapshots.find((c) => c.name === layout.activeCampaign);
  const rosterIds = (campaign?.rosterSourceIds || []).map((id) => id.replace(/^ddb-/, ""));
  const [scope, setScope] = useState<Scope>(rosterIds.length ? "campaign" : "open");
  const [refresh, setRefresh] = useState(true);
  const [openTargets, setOpenTargets] = useState<OpenTarget[] | null>(null);
  const [phase, setPhase] = useState<"idle" | "scanning" | "done" | "error">("idle");
  const [progress, setProgress] = useState<{ completed: number; total: number; detail: string }>({ completed: 0, total: 0, detail: "" });
  const [sheets, setSheets] = useState<SheetRead[]>([]);
  const [failures, setFailures] = useState<{ sourceId?: string; name?: string; reason?: string }[]>([]);
  const [error, setError] = useState("");
  // Prune: after a sync, remove campaign characters not open on D&D Beyond. The
  // DM chose the aggressive form — custom characters count too — so it is opt-in
  // and always shows a confirm (confirmPrune) listing exactly who before any
  // permanent removal.
  const [prune, setPrune] = useState(false);
  const [confirmPrune, setConfirmPrune] = useState<Character[] | null>(null);
  // The importer owns the trap only while the confirm is closed; when the confirm
  // opens it takes over, so the two are never trapping focus at the same time.
  const trap = useFocusTrap<HTMLDivElement>(onClose, !confirmPrune);
  const pruneTrap = useFocusTrap<HTMLDivElement>(() => setConfirmPrune(null), Boolean(confirmPrune));
  const scanId = useRef("");

  useEffect(() => {
    if (!bridge.connected) return;
    request<{ ok: boolean; openCount?: number; targets?: OpenTarget[] }>({ type: "DM_SCREEN_OPEN_SHEET_LIST_REQUEST", requestId: newRequestId("sheets") }, { resultType: "DM_SCREEN_OPEN_SHEET_LIST_RESULT", timeoutMs: 15000 })
      .then((r) => setOpenTargets(r.targets || []))
      .catch(() => setOpenTargets([]));
  }, [bridge.connected]);

  async function scan() {
    setPhase("scanning"); setError(""); setSheets([]); setFailures([]); setProgress({ completed: 0, total: 0, detail: "Asking the connector to open and read the sheets…" });
    scanId.current = newRequestId("scan");
    const targetIds = scope === "campaign" ? rosterIds : (openTargets || []).map((t) => t.sourceId);
    try {
      const result = await request<{ ok: boolean; sheets?: SheetRead[]; failures?: any[]; error?: string; cancelled?: boolean }>(
        { type: "DM_SCREEN_SCAN_REQUEST", scanId: scanId.current, scope, targetIds, refresh },
        { resultType: "DM_SCREEN_SCAN_RESULT", idKey: "scanId", timeoutMs: 10 * 60 * 1000, progressType: "DM_SCREEN_CHARACTER_SCAN_PROGRESS",
          onProgress: (m) => setProgress({ completed: Number(m.completed || 0), total: Number(m.total || 0), detail: String(m.detail || m.phase || "") }) },
      );
      if (!result.ok) { setPhase("error"); setError(result.error || (result.cancelled ? "Scan cancelled" : "The connector could not read the sheets")); return; }
      setSheets(result.sheets || []);
      setFailures(result.failures || []);
      setPhase("done");
    } catch (e) { setPhase("error"); setError(e instanceof Error ? e.message : "Scan failed"); }
  }

  function cancel() {
    if (scanId.current) window.postMessage({ source: "dm-screen-app", type: "DM_SCREEN_SCAN_CANCEL", scanId: scanId.current }, window.location.origin);
    setPhase("idle");
  }

  // Who a prune would remove: roster minus (open now ∪ just imported ∪ failed to
  // read). A failed read is "unknown", not "not open"; and with no open-tab
  // answer yet (openTargets === null) nothing is prunable. See prunableNotOpen.
  const pruneCandidates = () => (prune
    ? prunableNotOpen(campaignRoster(layout), openTargets, sheets.map((s) => s.sourceId), failures.map((f) => f.sourceId || "").filter(Boolean))
    : []);

  function importAll(removeIds: string[] = []) {
    update((l) => {
      const byId = new Map(l.characters.map((c) => [c.id, c]));
      const merged = sheets.map((s, i) => mergeSheet(byId.get(`ddb-${s.sourceId}`), s, byId.size + i));
      // An import is the sheet speaking: never write it straight back.
      // Straight off the sheet, so the sheet holds exactly this.
      merged.forEach((c) => noteSheetHolds(c.id, statusSignature(c)));
      merged.forEach((c) => byId.set(c.id, c));
      const characters = [...byId.values()];
      const campaignSnapshots = l.campaignSnapshots.map((snap) => snap.name === l.activeCampaign
        ? { ...snap, lastSyncedAt: new Date().toISOString(), characters: snap.characters.map((c) => byId.get(c.id) || c) }
        : snap);
      // The sheets just read belong to the campaign that is open. Without this
      // the snapshot claims nobody, every view falls back to "show everyone",
      // and the other table's characters stay in the tracker after a switch.
      const claimed = claimForCampaign({ ...l, characters, campaignSnapshots, selectedId: l.selectedId || merged[0]?.id || "" }, merged.map((c) => c.id));
      // Then drop the ones the DM confirmed are no longer at this table.
      return unclaimFromCampaign(claimed, removeIds);
    });
    if (sheets.length) logActivity({ text: `Imported ${sheets.length} character sheet${sheets.length === 1 ? "" : "s"} from D&D Beyond`, meta: sheets.map((s) => s.name).join(", "), tone: "green" });
    if (removeIds.length) logActivity({ text: `Removed ${removeIds.length} character${removeIds.length === 1 ? "" : "s"} not open on D&D Beyond`, meta: "they no longer belong to this campaign's roster", tone: "amber" });
    onClose();
  }

  // Import, but if pruning would remove anyone, confirm first.
  function finish() {
    const candidates = pruneCandidates();
    if (candidates.length) { setConfirmPrune(candidates); return; }
    importAll();
  }

  const candidates = phase === "done" ? pruneCandidates() : [];
  const primaryLabel = sheets.length
    ? `Import ${sheets.length}${candidates.length ? ` · remove ${candidates.length}` : ""}`
    : candidates.length ? `Remove ${candidates.length}` : "Import";

  return (
    <>
    <div className="modal-backdrop" onClick={onClose}>
      <div ref={trap} className="modal" role="dialog" aria-modal="true" aria-label="D&D Beyond live importer" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <div><span className="eyebrow">D&D Beyond live importer</span><h2>Scan character sheets</h2></div>
          <button className="icon" onClick={onClose} aria-label="Close importer">×</button>
        </header>
        <p className="muted">{bridge.connected ? `Browser bridge connected · v${bridge.version}` : "Waiting for the Compass Bridge connector… install it and reload this page."}</p>
        <fieldset className="scope">
          <legend>Scan scope</legend>
          <label><input type="radio" checked={scope === "campaign"} onChange={() => setScope("campaign")} disabled={!rosterIds.length} /> Current campaign ({rosterIds.length} synced PCs)</label>
          <label><input type="radio" checked={scope === "open"} onChange={() => setScope("open")} /> Open character tabs ({openTargets ? openTargets.length : "checking…"})</label>
          <label className="check"><input type="checkbox" checked={refresh} onChange={(e) => setRefresh(e.target.checked)} /> Reload sheets before reading (recommended)</label>
          <label className="check" title="After the import, permanently remove every character in this campaign's roster that is not open on D&D Beyond now (custom characters included). You confirm the exact list before anything is removed."><input type="checkbox" checked={prune} onChange={(e) => setPrune(e.target.checked)} /> Also remove campaign characters not open on D&D Beyond</label>
        </fieldset>
        {phase === "scanning" && (
          <div className="progress">
            <div className="bar"><span style={{ width: progress.total ? `${(100 * progress.completed) / progress.total}%` : "10%" }} /></div>
            <p>{progress.total ? `${progress.completed} / ${progress.total} · ` : ""}{progress.detail}</p>
          </div>
        )}
        {phase === "error" && <p className="error" role="alert">{error}</p>}
        {phase === "done" && (
          <div className="results">
            <p className="ok">{sheets.length} sheet{sheets.length === 1 ? "" : "s"} ready to import{failures.length ? ` · ${failures.length} failed` : ""}</p>
            <ul className="sheet-list">
              {sheets.map((s) => <li key={s.sourceId}><strong>{s.name}</strong> <span className="muted">{s.detail}</span> <span className="pill">{s.hp}/{s.maxHp} HP</span> <span className="pill">AC {s.ac}</span></li>)}
              {failures.map((f, i) => <li key={`f${i}`} className="error">{f.name || f.sourceId}: {f.reason}</li>)}
            </ul>
          </div>
        )}
        <footer className="modal-foot">
          {phase === "scanning" ? <button className="ghost" onClick={cancel}>Cancel scan</button> : <button className="ghost" onClick={onClose}>Close</button>}
          <button onClick={scan} disabled={!bridge.connected || phase === "scanning" || (scope === "campaign" ? !rosterIds.length : !(openTargets || []).length)}>Scan {scope === "campaign" ? "current campaign" : "open sheets"}</button>
          <button className="primary" onClick={finish} disabled={phase !== "done" || (!sheets.length && !candidates.length)}>{primaryLabel}</button>
        </footer>
      </div>
    </div>
    {confirmPrune && (
      <div className="modal-backdrop" onClick={() => setConfirmPrune(null)}>
        <div ref={pruneTrap} className="modal prompt-modal" role="alertdialog" aria-modal="true" aria-label="Confirm removals" onClick={(e) => e.stopPropagation()}>
          <header className="modal-head"><div><span className="eyebrow">Permanent</span><h2>Remove {confirmPrune.length} from {layout.activeCampaign || "this campaign"}?</h2></div></header>
          <p className="muted">These are not open on D&D Beyond, so they will be removed from this campaign's roster. A D&D Beyond sheet comes back if you sync with its tab open; a custom character does not.</p>
          <ul className="sheet-list">
            {confirmPrune.map((c) => <li key={c.id}><strong>{c.name}</strong> <span className="pill">{c.id.startsWith("ddb-") ? "D&D Beyond sheet" : "custom — not recoverable"}</span></li>)}
          </ul>
          <footer className="modal-foot">
            <button className="ghost" onClick={() => setConfirmPrune(null)}>Cancel</button>
            {sheets.length ? <button onClick={() => { setConfirmPrune(null); importAll(); }}>Import without removing</button> : null}
            <button className="primary" onClick={() => { const ids = confirmPrune.map((c) => c.id); setConfirmPrune(null); importAll(ids); }}>{sheets.length ? `Remove ${confirmPrune.length} & import` : `Remove ${confirmPrune.length}`}</button>
          </footer>
        </div>
      </div>
    )}
    </>
  );
}
